"use client";

import { track } from "@/lib/analytics/data-layer";
import Image from "next/image";
import NextLink from "next/link";

import { IMAGE_QUALITY, IMAGE_SIZES_PRESETS } from "@/lib/images/image-config";

/**
 * "Box duplo" — repeats the hero's guest/host bifurcation at the end of the
 * reading flow, so the decision sits at both ends of the page.
 *
 * Each subtitle line needs its own `[data-rise-wrap]`/`[data-rise]` pair: the
 * wrapper masks, the inner span slides. Breaking the pairing kills the reveal.
 *
 * The two labels are the reference's own — "Encontrar uma lista" and "Criar minha lista",
 * measured against it on 2026-09-24. They had read "Buscar uma lista" and "Começar agora"
 * since the first implementation, which is copy the design never wrote.
 */
export function DuoCtaSection() {
  return (
    <section id="criar" className="bg-[var(--tb-porcelain-tint)]">
      <div className="grid grid-cols-1 [@media(min-width:760px)]:grid-cols-2">
        <NextLink
          href="/listas"
          onClick={() =>
            track({
              event: "select_content",
              content_type: "cta",
              item_id: "guest_find_list",
              location: "duo_cta",
              journey: "guest",
              link_url: "/listas",
            })
          }
          className="group relative flex min-h-[clamp(440px,84svh,900px)] flex-col items-center justify-center overflow-hidden text-center text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2"
        >
          {/* The reference's portal stage (canon TPL-01): the photograph at scale(1.20), drifting by
              `--bannerPY` (factor 0.08, `useScrollEffects`), in its two crops — the tall one below 900px. */}
          <span
            data-banner-stage=""
            className="absolute inset-0 will-change-transform motion-reduce:!transform-none"
            style={{ transform: "translate3d(0, var(--bannerPY, 0px), 0) scale(1.20)" }}
          >
            <Image
              src="/images/homepage/presentear-mobile.jpg"
              alt="Presentear com Tania Bulhões"
              fill
              sizes={IMAGE_SIZES_PRESETS.duoPhoneCrop}
              quality={IMAGE_QUALITY.high}
              className="object-cover transition-[filter] duration-[360ms] ease-[var(--tb-ease-linen)] group-hover:brightness-[0.78] min-[900px]:hidden"
            />
            <Image
              src="/images/homepage/presentear.jpg"
              alt="Presentear com Tania Bulhões"
              fill
              sizes={IMAGE_SIZES_PRESETS.splitFromDesktop}
              quality={IMAGE_QUALITY.high}
              className="hidden object-cover transition-[filter] duration-[360ms] ease-[var(--tb-ease-linen)] group-hover:brightness-[0.78] min-[900px]:block"
            />
          </span>
          <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(61,63,57,0.30),rgba(61,63,57,0.52))]" />
          <span className="pointer-events-none relative z-[1] flex max-w-[min(440px,80%)] flex-col items-center gap-[clamp(16px,1.5vw,24px)]">
            <span className="font-display text-[clamp(28px,3.17vw,48px)] font-light uppercase leading-[1.1] text-white">Presentear</span>
            <span className="flex flex-col items-center text-[clamp(15px,1.15vw,17px)] leading-[1.6] text-white">
              <span data-rise-wrap="1" className="block overflow-hidden pb-px"><span data-rise="1">Encontre presentes que encantam</span></span>
              <span data-rise-wrap="1" className="block overflow-hidden pb-px"><span data-rise="2">e ficam na memória.</span></span>
            </span>
          </span>
          <span className="pointer-events-none absolute bottom-[clamp(28px,4vw,54px)] z-[1] inline-flex items-center gap-3 border border-[rgba(255,255,255,0.68)] px-[30px] py-[14px] text-[12px] uppercase tracking-[0.14em] text-white transition-[background,border-color,backdrop-filter] duration-[400ms] group-hover:border-[rgba(255,255,255,0.95)] group-hover:bg-[rgba(255,255,255,0.18)] group-hover:[backdrop-filter:blur(10px)]">
            Encontrar uma lista
          </span>
        </NextLink>

        <NextLink
          href="/onboarding"
          onClick={() =>
            track({
              event: "select_content",
              content_type: "cta",
              item_id: "host_create_list",
              location: "duo_cta",
              journey: "host",
              link_url: "/onboarding",
            })
          }
          className="group relative flex min-h-[clamp(440px,84svh,900px)] flex-col items-center justify-center overflow-hidden text-center text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2"
        >
          {/* The reference's portal stage (canon TPL-01): the photograph at scale(1.20), drifting by
              `--bannerPY` (factor 0.08, `useScrollEffects`), in its two crops — the tall one below 900px. */}
          <span
            data-banner-stage=""
            className="absolute inset-0 will-change-transform motion-reduce:!transform-none"
            style={{ transform: "translate3d(0, var(--bannerPY, 0px), 0) scale(1.20)" }}
          >
            <Image
              src="/images/homepage/crie-sua-lista-mobile.jpg"
              alt="Crie a sua lista Tania Bulhões"
              fill
              sizes={IMAGE_SIZES_PRESETS.duoPhoneCrop}
              quality={IMAGE_QUALITY.high}
              className="object-cover transition-[filter] duration-[360ms] ease-[var(--tb-ease-linen)] group-hover:brightness-[0.78] min-[900px]:hidden"
            />
            <Image
              src="/images/homepage/crie-sua-lista.jpg"
              alt="Crie a sua lista Tania Bulhões"
              fill
              sizes={IMAGE_SIZES_PRESETS.splitFromDesktop}
              quality={IMAGE_QUALITY.high}
              className="hidden object-cover transition-[filter] duration-[360ms] ease-[var(--tb-ease-linen)] group-hover:brightness-[0.78] min-[900px]:block"
            />
          </span>
          <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(61,63,57,0.30),rgba(61,63,57,0.52))]" />
          <span className="pointer-events-none relative z-[1] flex max-w-[min(440px,80%)] flex-col items-center gap-[clamp(16px,1.5vw,24px)]">
            <span className="font-display text-[clamp(28px,3.17vw,48px)] font-light uppercase leading-[1.1] text-white">Crie sua lista</span>
            <span className="flex flex-col items-center text-[clamp(15px,1.15vw,17px)] leading-[1.6] text-white">
              <span data-rise-wrap="1" className="block overflow-hidden pb-px"><span data-rise="1">Uma curadoria inteiramente sua, com tudo</span></span>
              <span data-rise-wrap="1" className="block overflow-hidden pb-px"><span data-rise="2">que você deseja levar para a sua nova vida.</span></span>
            </span>
          </span>
          <span className="pointer-events-none absolute bottom-[clamp(28px,4vw,54px)] z-[1] inline-flex items-center gap-3 border border-[rgba(255,255,255,0.68)] px-[30px] py-[14px] text-[12px] uppercase tracking-[0.14em] text-white transition-[background,border-color,backdrop-filter] duration-[400ms] group-hover:border-[rgba(255,255,255,0.95)] group-hover:bg-[rgba(255,255,255,0.18)] group-hover:[backdrop-filter:blur(10px)]">
            Criar minha lista
          </span>
        </NextLink>
      </div>
    </section>
  );
}
